import { Coins, Map, Megaphone, ScrollText, Swords, Users } from 'lucide-react'
import { data, Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { SectionTitle } from '#app/components/ui/section-title.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [{ title: 'Hub | Le Monastère' }]

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)

	if (!userId) {
		return data({
			characters: [],
			isAdmin: false,
			groupResources: [],
			announcements: [],
		})
	}

	// Server-side admin check
	const user = await prisma.user.findUnique({
		where: { id: userId },
		select: {
			roles: {
				select: {
					name: true,
					permissions: {
						select: { entity: true, action: true, access: true },
					},
				},
			},
		},
	})

	const isAdmin = user?.roles.some((r) => r.name === 'admin') ?? false

	const characters = await prisma.character.findMany({
		where: { userId },
		orderBy: [{ mainCharacter: 'desc' }, { createdAt: 'asc' }],
		select: {
			id: true,
			name: true,
			totalXp: true,
			mainCharacter: true,
			career: {
				select: { name: true },
			},
			group: {
				select: {
					id: true,
					name: true,
					_count: { select: { characters: true } },
					territory: {
						select: {
							name: true,
							baseIncome: true,
							defensePoints: true,
							_count: { select: { buildings: true } },
						},
					},
				},
			},
		},
	})

	// Primary character (main or first) used for group/resource/announcement queries
	const character = characters[0] ?? null

	// Admin-only: currency resources for the character's group
	const groupResources =
		isAdmin && character?.group
			? await prisma.groupResource.findMany({
					where: {
						groupId: character.group.id,
						resourceType: {
							in: [
								'MONNAIE_CORS_ETAIN',
								'MONNAIE_COURONNE_ARGENT',
								'MONNAIE_LYS_OR',
							],
						},
					},
					select: { resourceType: true, quantity: true },
				})
			: []

	// Annonces: recent planned/in-progress events for the group
	const announcements = character?.group
		? await prisma.groupEvent.findMany({
				where: {
					groupId: character.group.id,
					status: { in: ['PLANNED', 'IN_PROGRESS'] },
				},
				orderBy: { eventDate: 'desc' },
				take: 3,
				select: { id: true, name: true, eventDate: true, status: true },
			})
		: []

	return data({ characters, isAdmin, groupResources, announcements })
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const user = useOptionalUser()
	const { characters, isAdmin, groupResources, announcements } = loaderData
	const character = characters[0] ?? null
	const hasMultiple = characters.length > 1

	const getResourceQty = (type: string) =>
		groupResources.find((r) => r.resourceType === type)?.quantity ?? 0

	if (user) {
		return (
			<main className='container py-8'>
				<div className='grid gap-6 lg:grid-cols-3'>
					<div className='grid gap-6 lg:col-span-2'>
						{/* Personnage(s) */}
						<Card>
							<CardHeader>
								<CardTitle className='flex items-center justify-between gap-2'>
									<SectionTitle icon={<Swords className='h-5 w-5' />}>
										{hasMultiple ? 'Vos personnages' : 'Votre personnage'}
									</SectionTitle>
									<Button variant='default' size='sm' asChild>
										<Link to='/character/new'>Créer</Link>
									</Button>
								</CardTitle>
								{!character && (
									<CardDescription>
										Créez votre personnage pour commencer l'aventure
									</CardDescription>
								)}
							</CardHeader>
							<CardContent>
								{characters.length > 0 ? (
									<ul className='space-y-2'>
										{characters.map((c) => (
											<li key={c.id}>
												<Link
													to={`/character?characterId=${c.id}`}
													className='hover:bg-muted flex items-center gap-2 rounded-lg p-2 transition-colors'
												>
													<div className='min-w-0 flex-1'>
														<div className='flex items-center gap-2'>
															<span className='font-medium'>{c.name}</span>
															{c.mainCharacter && (
																<Badge variant='outline' className='text-xs'>
																	Principal
																</Badge>
															)}
														</div>
														<p className='text-muted-foreground text-sm'>
															{c.career?.name ?? 'Aucune carrière'} •{' '}
															{c.totalXp} XP
														</p>
													</div>
												</Link>
											</li>
										))}
									</ul>
								) : (
									<p className='text-muted-foreground text-sm'>
										Un personnage est nécessaire pour participer aux événements
									</p>
								)}
							</CardContent>
						</Card>

						{/* Projets en cours */}
						<Card>
							<CardHeader>
								<CardTitle className='flex items-center justify-between gap-2'>
									<SectionTitle icon={<ScrollText className='h-5 w-5' />}>
										Projets en cours
									</SectionTitle>
									<Button variant='default' size='sm' asChild>
										<Link to='/projects'>Voir</Link>
									</Button>
								</CardTitle>
							</CardHeader>
							<CardContent>
								<p className='text-muted-foreground text-sm'>
									Consultez vos projets et propositions en cours
								</p>
							</CardContent>
						</Card>
					</div>

					<div className='grid gap-6'>
						<Card>
							<CardHeader>
								<CardTitle className='flex items-center justify-between gap-2'>
									<SectionTitle icon={<Users className='h-5 w-5' />}>
										Groupe
									</SectionTitle>
									<Button variant='default' size='sm' asChild>
										<Link to='/group'>Voir</Link>
									</Button>
								</CardTitle>
							</CardHeader>
							<CardContent className='space-y-4'>
								{character?.group ? (
									<div className='flex items-center justify-between gap-3'>
										<p className='text-foreground truncate text-lg font-semibold'>
											{character.group.name}
										</p>
										<Badge
											variant='secondary'
											className='flex shrink-0 items-center gap-1.5'
										>
											<Users className='h-3.5 w-3.5' />
											{character.group._count.characters}
										</Badge>
									</div>
								) : (
									<p className='text-muted-foreground text-sm'>
										Vous n'appartenez à aucun groupe
									</p>
								)}

								{isAdmin && character?.group && (
									<>
										<div className='border-border/60 space-y-4 border-t pt-4'>
											<div className='flex items-center justify-between gap-2'>
												<div className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase'>
													<Coins className='h-3.5 w-3.5' />
													Ressources
												</div>
												<Link
													to='/geopolitics'
													className='text-muted-foreground hover:text-foreground text-xs font-medium underline-offset-4 hover:underline'
												>
													Voir tout
												</Link>
											</div>
											{groupResources.length > 0 ? (
												<div className='grid grid-cols-3 gap-2'>
													<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
														<p className='text-foreground text-lg font-semibold'>
															{getResourceQty('MONNAIE_CORS_ETAIN')}
														</p>
														<p className='text-muted-foreground text-xs'>
															Étain
														</p>
													</div>
													<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
														<p className='text-foreground text-lg font-semibold'>
															{getResourceQty('MONNAIE_COURONNE_ARGENT')}
														</p>
														<p className='text-muted-foreground text-xs'>
															Couronnes
														</p>
													</div>
													<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
														<p className='text-foreground text-lg font-semibold'>
															{getResourceQty('MONNAIE_LYS_OR')}
														</p>
														<p className='text-muted-foreground text-xs'>
															Lys d'Or
														</p>
													</div>
												</div>
											) : (
												<p className='text-muted-foreground text-sm'>
													Aucune ressource enregistrée
												</p>
											)}
										</div>

										<div className='border-border/60 space-y-4 border-t pt-4'>
											<div className='flex items-center justify-between gap-2'>
												<div className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase'>
													<Map className='h-3.5 w-3.5' />
													Terres & Rendements
												</div>
												<Link
													to='/geopolitics'
													className='text-muted-foreground hover:text-foreground text-xs font-medium underline-offset-4 hover:underline'
												>
													Géopolitique
												</Link>
											</div>
											{character.group.territory ? (
												<div className='mt-2 space-y-3'>
													<div className='grid grid-cols-3 gap-2 text-center'>
														<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
															<p className='text-foreground text-lg font-semibold'>
																{character.group.territory.baseIncome}
															</p>
															<p className='text-muted-foreground text-xs'>
																Revenu
															</p>
														</div>
														<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
															<p className='text-foreground text-lg font-semibold'>
																{character.group.territory._count.buildings}/3
															</p>
															<p className='text-muted-foreground text-xs'>
																Bâtiments
															</p>
														</div>
														<div className='bg-secondary/50 rounded-lg px-3 py-2 text-center'>
															<p className='text-foreground text-lg font-semibold'>
																{character.group.territory.defensePoints}
															</p>
															<p className='text-muted-foreground text-xs'>
																Défense
															</p>
														</div>
													</div>
												</div>
											) : (
												<p className='text-muted-foreground text-sm'>
													Aucune terre attribuée
												</p>
											)}
										</div>
									</>
								)}
							</CardContent>
						</Card>

						{/* Annonces */}
						<Card>
							<CardHeader>
								<CardTitle>
									<SectionTitle icon={<Megaphone className='h-5 w-5' />}>
										Annonces
									</SectionTitle>
								</CardTitle>
							</CardHeader>
							<CardContent>
								{announcements.length > 0 ? (
									<ul className='space-y-3'>
										{announcements.map((event) => (
											<li
												key={event.id}
												className='flex items-start justify-between gap-2'
											>
												<div>
													<p className='text-foreground text-sm font-medium'>
														{event.name}
													</p>
													<p className='text-muted-foreground text-xs'>
														{new Date(event.eventDate).toLocaleDateString(
															'fr-FR',
															{
																day: 'numeric',
																month: 'long',
																year: 'numeric',
															},
														)}
													</p>
												</div>
												<Badge
													variant={
														event.status === 'IN_PROGRESS'
															? 'default'
															: 'secondary'
													}
												>
													{event.status === 'IN_PROGRESS'
														? 'En cours'
														: 'Planifié'}
												</Badge>
											</li>
										))}
									</ul>
								) : (
									<p className='text-muted-foreground text-sm'>
										Aucune annonce pour le moment
									</p>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</main>
		)
	}

	return (
		<main className='grid h-full place-items-center'>
			<div className='flex flex-col items-center gap-6 px-4 py-16 text-center'>
				<div className='relative flex items-center justify-center'>
					<div className='absolute h-[270px] w-[270px] rounded-full bg-[rgba(45,21,69,1)]' />
					<img
						src='/img/logo-enarque.svg'
						alt=''
						className='relative h-[300px] w-[300px] translate-y-7'
					/>
				</div>
				<h1 className='font-display text-foreground text-1xl p-0 font-medium'>
					Les Énarques de Del'dorym
				</h1>
				<p className='font-display text-foreground p-0 text-5xl'>
					Le Monastère
				</p>
				<p className='text-muted-foreground text-sm'>
					Bienvenue au Monastère. Connectez-vous pour accéder à votre espace.
				</p>
			</div>
		</main>
	)
}
